import { useCallback, useEffect, useState } from 'react';

import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  Fab,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import RefreshIcon from '@material-ui/icons/Refresh';
import { useModal } from 'react-modal-hook';

import DialogHeader from 'components/DialogHeader';
import * as socketEvents from 'constants/socketEvents';
import socket from 'services/socket';
import { CanalInterface, TipoConexao } from 'store/reducers/canais';

import ModalCodeError from './ModalCodeError';

type ModalQRCodeProps = {
  open: boolean;
  onClose: () => void;
  canal: CanalInterface | undefined;
  isProcessarMensagensAntigas: boolean;
};

const useStyles = makeStyles(({ palette, shape, spacing }) => ({
  dialogContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    padding: spacing(3, 0),
  },
  fab: {
    width: spacing(35),
    height: spacing(35),
    padding: spacing(4),
    '& > .MuiFab-label': {
      flexDirection: 'column',
    },
  },
  qrCode: {
    width: spacing(35),
    height: spacing(35),
    borderRadius: shape.borderRadius,
    backgroundColor: palette.common.white,
    border: `1px solid ${palette.text.disabled}`,
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  image: {
    width: spacing(24),
    height: spacing(24),
    pointerEvents: 'none',
    userSelect: 'none',
  },
  chip: {
    position: 'absolute',
    bottom: spacing(-2),
    backgroundColor: palette.background.tertiary,
  },
}));

export default function ModalQRCode(props: ModalQRCodeProps) {
  const { open, onClose, canal, isProcessarMensagensAntigas } = props;
  const classes = useStyles();
  const [loadingQrCode, setLoadingQrCode] = useState(true);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [qrCodeTimeout, setQrCodeTimeout] = useState(false);
  const [qrCodeError, setQrCodeError] = useState('');

  const handleClose = useCallback(() => {
    if (onClose) onClose();
  }, [onClose]);

  // Após fechar o modal envia o emit cancelando o fecebimento de qr codes
  const handleExited = () => {
    if (canal) {
      const { canalId } = canal;
      const data = { canalId, action: 'leave' };

      socket.emit(socketEvents.EMIT_ROOM_QR_CODE, data);
    }

    setQrCodeUrl('');
    setQrCodeTimeout(false);
    setLoadingQrCode(false);
  };

  // Tenta reconectar o canal
  const handleClickButtonTimeout = () => {
    if (canal) {
      const { canalId } = canal;
      socket.emit(socketEvents.EMIT_RECONECTAR_CANAL, {
        canalId,
        tipoConexao: TipoConexao.WHATSAPP_QRCODE,
        isProcessarMensagensAntigas,
      });

      setLoadingQrCode(true);
      setQrCodeTimeout(false);
      setQrCodeUrl('');
    }
  };

  const [showModalCodeError, hideModalCodeError] = useModal(
    ({ in: open }) => (
      <ModalCodeError
        open={open}
        onClose={hideModalCodeError}
        error={qrCodeError}
      />
    ),
    [qrCodeError],
  );

  useEffect(() => {
    const handleNewQRCode = ({ url }: { url: string }) => {
      setQrCodeUrl(url);
      setLoadingQrCode(false);
      setQrCodeTimeout(false);
    };
    const handleQrCodeTimeout = () => {
      setQrCodeUrl('');
      setQrCodeTimeout(true);
      setLoadingQrCode(false);
    };
    const handleConectado = () => {
      handleClose();
    };
    const handleNaoConectado = ({ message }: { message: string }) => {
      setQrCodeError(message);

      handleClose();
      showModalCodeError();
    };

    socket.on(socketEvents.ON_WHATSAPP_NEW_QR_CODE, handleNewQRCode);
    socket.on(socketEvents.ON_WHATSAPP_CONECTADO, handleConectado);
    socket.on(socketEvents.ON_WHATSAPP_NAO_CONECTADO, handleNaoConectado);
    socket.on(socketEvents.ON_WHATSAPP_QR_CODE_TIMEOUT, handleQrCodeTimeout);

    return () => {
      socket.off(socketEvents.ON_WHATSAPP_NEW_QR_CODE, handleNewQRCode);
      socket.off(socketEvents.ON_WHATSAPP_CONECTADO, handleConectado);
      socket.off(socketEvents.ON_WHATSAPP_NAO_CONECTADO, handleNaoConectado);
      socket.off(socketEvents.ON_WHATSAPP_QR_CODE_TIMEOUT, handleQrCodeTimeout);
    };
  }, [handleClose, showModalCodeError]);

  return (
    <Dialog
      open={open}
      disableEscapeKeyDown
      onClose={(event, reason) => {
        if (reason === 'backdropClick') return;
        handleClose();
      }}
      TransitionProps={{ onExited: handleExited }}
      maxWidth="xs"
      fullWidth
    >
      <DialogHeader
        title={
          <Box component="span" style={{ fontWeight: 700 }}>
            Conectar canal
          </Box>
        }
        onClose={handleClose}
      />

      <DialogContent className={classes.dialogContent}>
        {qrCodeTimeout && (
          <Fab
            className={classes.fab}
            onClick={handleClickButtonTimeout}
            variant="circular"
            color="primary"
          >
            <RefreshIcon fontSize="large" />
            <Typography component={Box} mt={3}>
              CLIQUE PARA RECARREGAR O CÓDIGO QR
            </Typography>
          </Fab>
        )}

        {!qrCodeTimeout && (
          <Box className={classes.qrCode}>
            {loadingQrCode && <CircularProgress />}

            {!loadingQrCode && qrCodeUrl && (
              <img className={classes.image} alt="QRCode" src={qrCodeUrl} />
            )}

            {!loadingQrCode && canal && (
              <Chip className={classes.chip} label={canal.numeroContato} />
            )}
          </Box>
        )}

        {!loadingQrCode && qrCodeUrl && (
          <Box mt={6}>
            <Typography variant="body2" color="textSecondary">
              Aponte o seu celular para o código acima.
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
